import { useQuery } from "data/hooks/useQuery";
import PagedDataResultDTO from "features/shared/dtos/PagedDataResultDTO";
import PaymentDTO from "features/payments/dtos/PaymentDTO";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import CustomerState from "infrastructure/store/customer/CustomerState";
import { useEffect, useState } from "react";
import IExternalFilterData from "features/shared/components/dataFilter/IExternalFilterData";
import {
  Customer_Payments,
  Customer_Payments_AmountLimits,
} from "data/endpoints";
import DataQuery from "data/DataQuery";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { appendQueryParams } from "features/shared/helpers/queryParams";
import ConditionalExecute from "data/models/ConditionalExecute";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IRepository from "data/IRepository";
import AmountLimitDTO from "features/shared/dtos/AmountLimitDTO";
import { IocTypes } from "infrastructure/ioc/ioc.types";

export const usePaymentsOnDetail = (
  filterByName: string,
  filterByValue: string,
  gridQueryParams: string,
  gridConditionalExecute: ConditionalExecute,
  proformaInvoiceId?: string
) => {
  //#region DI
  const paymentAmountLimitsRepo = useInjection<IRepository<AmountLimitDTO>>(
    IocTypes.Repository
  );
  //#endregion DI

  //#region store
  const customer = useSelector<IAppState, CustomerState>(
    (store) => store.customer
  ).customer;
  //#endregion store

  //#region state
  const [externalFilterData, setExternalFilterData] = useState<
    IExternalFilterData<any>[]
  >([]);
  //#endregion state

  //#region helpers
  const getSearchParams = () => {
    let params = appendQueryParams(
      new URLSearchParams(gridQueryParams),
      filterByName,
      filterByValue
    );

    if (proformaInvoiceId) {
      appendQueryParams(params, "ProformaInvoiceId", proformaInvoiceId);
    }

    return params;
  };
  //#endregion helpers

  //#region fetch data
  const query: DataQuery = {
    url: Customer_Payments,
    params: getSearchParams(),
    routeParams: getRouteParams("customerId", customer.customerId!),
  };
  const { data: payments, loadStatus: paymentsLoadStatus } = useQuery<
    PagedDataResultDTO<PaymentDTO>
  >(
    query,
    { count: 0, result: [] },
    [gridQueryParams, customer.customerId],
    { useTracker: true },
    gridConditionalExecute
  );

  const fetchAndSetPaymentsExternalFilterData = async () => {
    let externalFilterData: IExternalFilterData<any>[] = [];

    // payment Amount Limits
    const paymentAmountLimits = await paymentAmountLimitsRepo.get({
      url: Customer_Payments_AmountLimits,
      routeParams: getRouteParams("customerId", customer.customerId!),
    });

    const currencyCodes = [
      ...new Set(paymentAmountLimits.map((x) => x.currencyCode)),
    ];

    externalFilterData.push({
      name: "CurrencyCode",
      data: currencyCodes.map((val) => {
        return { currencyCode: val };
      }),
    });

    externalFilterData.push({
      name: "Amount",
      data: paymentAmountLimits.filter((x) => x.limitType === "Amount"),
    });

    externalFilterData.push({
      name: "AmountUsed",
      data: paymentAmountLimits.filter((x) => x.limitType === "AmountUsed"),
    });

    // set
    setExternalFilterData(externalFilterData);
  };
  //#endregion fetch data

  //#region side-effects
  useEffect(() => {
    fetchAndSetPaymentsExternalFilterData();
  }, []);
  //#endregion side-effects

  //#region return
  return { payments, paymentsLoadStatus, externalFilterData };
  //#endregion return
};
